import { ComponentRef, FormEvent, ReactElement, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Alert, Button, ErrorSummary, Paragraph } from '@digdir/designsystemet-react';
import type { BusinessProfileError, SavedBusinessProfile } from '../../../api/businessProfile';
import { useSaveBusinessProfileMutation } from '../../../hooks/queries/organization/useBusinessProfileQuery';
import { FieldError, validationErrors } from '../../WorkEnvironmentSurveyDetail/assessmentView';
import { CommitteeSection } from './CommitteeSection';
import { CompanySection } from './CompanySection';
import { ContactsSection } from './ContactsSection';
import {
  BusinessForm,
  businessForm,
  fieldErrors,
  formErrors,
  fromSignupText,
  savedText,
  toProfile,
} from './businessView';
import classes from './OrganizationBusinessPage.module.css';

/** The formal facts about the business, in three cards and saved together */
export const BusinessProfileForm = ({ saved }: { saved: SavedBusinessProfile }): ReactElement => {
  const { t, i18n } = useTranslation();
  const [form, setForm] = useState<BusinessForm>(() => businessForm(saved));
  const [errors, setErrors] = useState<FieldError[]>([]);
  const [isSaved, setIsSaved] = useState(false);
  const summary = useRef<ComponentRef<typeof ErrorSummary>>(null);
  const save = useSaveBusinessProfileMutation();

  useEffect(() => {
    if (errors.length > 0) summary.current?.focus();
  }, [errors]);

  const onChange = (changes: Partial<BusinessForm>) => {
    setIsSaved(false);
    setForm((current) => ({ ...current, ...changes }));
  };

  const submit = (event: FormEvent) => {
    event.preventDefault();
    const found = fieldErrors(formErrors(form), t);
    setErrors(found);
    if (found.length > 0) return;
    save.mutate(toProfile(form), {
      onSuccess: () => {
        setIsSaved(true);
        window.scrollTo(0, 0);
      },
      onError: (error) => setErrors(fieldErrors(validationErrors<BusinessProfileError>(error) ?? [], t)),
    });
  };

  const sectionProps = { form, onChange, errors };

  return (
    <form className={classes.form} onSubmit={submit} noValidate>
      {isSaved && (
        <Alert data-color="success" role="status">
          <Paragraph>{t('organizationBusiness.savedAlert')}</Paragraph>
        </Alert>
      )}
      {errors.length > 0 && (
        <ErrorSummary ref={summary}>
          <ErrorSummary.Heading>{t('organizationBusiness.errors.title')}</ErrorSummary.Heading>
          <ErrorSummary.List>
            {errors.map((error) => (
              <ErrorSummary.Item key={error.message}>
                <ErrorSummary.Link href={`#${error.fieldId}`}>{error.message}</ErrorSummary.Link>
              </ErrorSummary.Item>
            ))}
          </ErrorSummary.List>
        </ErrorSummary>
      )}
      {save.isError && errors.length === 0 && (
        <Alert data-color="danger" role="alert">
          <Paragraph>{t('organizationBusiness.saveFailed')}</Paragraph>
        </Alert>
      )}
      <Paragraph data-size="sm" className={classes.intro}>
        {t('organizationBusiness.intro')}
      </Paragraph>
      <CompanySection {...sectionProps} fromSignup={isSaved ? null : fromSignupText(saved, t, i18n.language)} />
      <ContactsSection {...sectionProps} mainSafetyReps={saved.mainSafetyReps} />
      <CommitteeSection {...sectionProps} />
      <div className={classes.footer}>
        <Button type="submit" loading={save.isPending}>
          {t('organizationBusiness.save')}
        </Button>
        <Paragraph data-size="sm" className={classes.hint}>
          {savedText(saved.profile?.updatedAt ?? null, new Date(), t, i18n.language)}
        </Paragraph>
      </div>
    </form>
  );
};
